import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../api/authApi';
import { User, Mail, Lock, Sliders, Bell, Save, Link as LinkIcon, Users, Info, Eye, CheckCircle2, Check, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';

const Settings = () => {
  const { user, updateUser } = useAuth();

  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    avatar: user?.avatar || ''
  });

  const [settingsData, setSettingsData] = useState({
    defaultEmployeeLimit: user?.settings?.defaultEmployeeLimit || 50,
    emailNotifications: user?.settings?.emailNotifications ?? true,
    systemAlerts: user?.settings?.systemAlerts ?? true,
    theme: user?.settings?.theme || 'dark'
  });

  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPass, setLoadingPass] = useState(false);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoadingProfile(true);
    try {
      const res = await authApi.updateProfile({
        ...profileData,
        settings: settingsData
      });
      updateUser(res.data);
      toast.success('Profile and platform settings saved!');
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoadingPass(true);
    try {
      await authApi.changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      });
      toast.success('Password updated successfully!');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setLoadingPass(false);
    }
  };

  return (
    <div className="space-y-8 animate-fadeIn w-full max-w-7xl mx-auto">
      <div className="pl-4 border-l-4 border-emerald-600 rounded-sm">
        <h2 className="text-xl font-extrabold text-slate-900 font-display">Platform & Account Settings</h2>
        <p className="text-xs text-slate-500 mt-1 font-medium">Manage Super Admin credentials and platform default preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left Form: Profile & Platform */}
        <div className="lg:col-span-2">
          <form onSubmit={handleProfileSubmit} className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-8">
            
            {/* Section 1 */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-slate-900 font-display">Super Admin Profile</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Update your profile information</p>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-2">Full Name</label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                    <input
                      type="text"
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-2">Avatar Image URL</label>
                  <div className="relative">
                    <LinkIcon className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                    <input
                      type="text"
                      value={profileData.avatar}
                      onChange={(e) => setProfileData({ ...profileData, avatar: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="h-px bg-slate-100 w-full" />

            {/* Section 2 */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Sliders className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-slate-900 font-display">Platform Defaults</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Configure default platform preferences</p>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <div className="flex items-center space-x-1.5 mb-2">
                    <label className="block text-xs font-semibold text-slate-700">Default Employee Limit for New Companies</label>
                    <Info className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <div className="relative">
                    <Users className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                    <input
                      type="number"
                      min="1"
                      value={settingsData.defaultEmployeeLimit}
                      onChange={(e) => setSettingsData({ ...settingsData, defaultEmployeeLimit: Number(e.target.value) })}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                    />
                  </div>
                </div>

                <div 
                  className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors cursor-pointer bg-slate-50/50"
                  onClick={() => setSettingsData({ ...settingsData, systemAlerts: !settingsData.systemAlerts })}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-white border border-slate-100 flex items-center justify-center shadow-sm">
                      <Bell className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-slate-900">Real-time System Alerts</p>
                      <p className="text-[11px] text-slate-500 mt-0.5 font-medium">Push notifications for limit requests & messages</p>
                    </div>
                  </div>
                  <div className={`w-11 h-6 rounded-full flex items-center p-1 transition-colors ${settingsData.systemAlerts ? 'bg-gradient-to-r from-emerald-600 to-teal-600' : 'bg-slate-300'}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform ${settingsData.systemAlerts ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between pt-2 gap-4">
              <button
                type="submit"
                disabled={loadingProfile}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold text-sm shadow-lg shadow-emerald-600/30 flex items-center space-x-2 transition-all disabled:opacity-50 w-fit"
              >
                <Save className="w-4 h-4" />
                <span>{loadingProfile ? 'Saving...' : 'Save Profile & Settings'}</span>
              </button>
              
              <div className="flex items-center space-x-1.5 text-xs font-semibold text-emerald-600">
                <CheckCircle2 className="w-4 h-4" />
                <span>Changes are saved securely</span>
              </div>
            </div>
          </form>
        </div>

        {/* Right Form: Password Change */}
        <div className="lg:col-span-1">
          <form onSubmit={handlePasswordSubmit} className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-6">
            <div className="flex items-center space-x-4">
              <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-slate-900 font-display">Security & Password</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">Update your password to keep your account secure</p>
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Current Password</label>
                <div className="relative">
                  <input
                    type="password"
                    placeholder="Enter current password"
                    required
                    value={passwords.currentPassword}
                    onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all placeholder:text-slate-300"
                  />
                  <Eye className="w-4 h-4 text-slate-300 absolute right-4 top-3 cursor-pointer hover:text-slate-500" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">New Password</label>
                <div className="relative">
                  <input
                    type="password"
                    placeholder="Enter new password"
                    required
                    value={passwords.newPassword}
                    onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all placeholder:text-slate-300"
                  />
                  <Eye className="w-4 h-4 text-slate-300 absolute right-4 top-3 cursor-pointer hover:text-slate-500" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Confirm New Password</label>
                <div className="relative">
                  <input
                    type="password"
                    placeholder="Confirm new password"
                    required
                    value={passwords.confirmPassword}
                    onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all placeholder:text-slate-300"
                  />
                  <Eye className="w-4 h-4 text-slate-300 absolute right-4 top-3 cursor-pointer hover:text-slate-500" />
                </div>
              </div>
            </div>

            <div className="bg-emerald-50/70 rounded-xl p-5 space-y-3 border border-emerald-100">
              <div className="flex items-center space-x-2 text-emerald-700 font-semibold text-xs">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>Password must contain:</span>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2 text-emerald-700 text-[11px] font-medium">
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Minimum 8 characters</span>
                </li>
                <li className="flex items-center space-x-2 text-emerald-700 text-[11px] font-medium">
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                  <span>At least one uppercase letter</span>
                </li>
                <li className="flex items-center space-x-2 text-emerald-700 text-[11px] font-medium">
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                  <span>At least one number or special character</span>
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loadingPass}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold text-sm shadow-lg shadow-emerald-600/30 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 mt-4"
            >
              <Lock className="w-4 h-4" />
              <span>{loadingPass ? 'Updating...' : 'Change Password'}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
